import { createStorefrontApiClient, customFetchApi } from '@shopify/storefront-api-client';
import { useEffect, useMemo, useState } from 'react';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import Button from '@mui/material/Button';
import ProductDetail from '@Components/Shop/ProductDetail'
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Modal from '@Components/Shared/Modals/Modal';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { TextField, Stack } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { ThemedButton } from '@Components/MuiWrappers/Button';
import ListingControlBar from '@Components/Listings/ListingControlBar';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import TranslationText from '@Components/Utilities/TranslationText';
import SearchContainer from '@Components/Shop/SearchContainer';
import CartButton from '@Components/Shop/CartButton';
import Cart from '@Components/Shop/Cart';
import ProductListing from '@Components/Shop/ProductListing';
import Typography from '@mui/material/Typography';
import {
    getProductsQuery, getCartQuery, cartLinesUpdateMutation, cartCreateMutation, cartLinesRemoveMutation, cartLinesAddMutation,
} from '@Components/Utilities/ShopQueries';
import { insertURLParameter, removeURLParameter } from '@Components/Utilities/SandhillsUtility';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import { styled } from '@mui/material/styles';

const SORT_OPTIONS = [
    { Key: 1, Value: 'Best Selling', sortKey: 'BEST_SELLING', reverse: false },
    { Key: 2, Value: 'Price: Low to High', sortKey: 'PRICE', reverse: false },
    { Key: 3, Value: 'Price: High to Low', sortKey: 'PRICE', reverse: true },
    { Key: 4, Value: 'Title: A-Z', sortKey: 'TITLE', reverse: false },
    { Key: 5, Value: 'Title: Z-A', sortKey: 'TITLE', reverse: true },
    { Key: 6, Value: 'Newest Arrivals', sortKey: 'CREATED_AT', reverse: true },
    { Key: 7, Value: 'Oldest Arrivals', sortKey: 'CREATED_AT', reverse: false },
    { Key: 8, Value: 'Vendor: A-Z', sortKey: 'VENDOR', reverse: false },
    { Key: 9, Value: 'Vendor: Z-A', sortKey: 'VENDOR', reverse: true },
    { Key: 10, Value: 'Product Type: A-Z', sortKey: 'PRODUCT_TYPE', reverse: false },
    { Key: 11, Value: 'Product Type: Z-A', sortKey: 'PRODUCT_TYPE', reverse: true }
];

const ProductCount = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: '.9rem',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
})); 

const ShopView = (props) => {
    const client = useMemo(() => {
        return createStorefrontApiClient({
            storeDomain: props.StoreDomain,
            apiVersion: '2026-07',
            publicAccessToken: props.PublicAccessToken,
            customFetchApi: (url, init) => {
                if (typeof window === 'undefined') {
                    // Server-side fetch
                    return fetch(url, init);
                }
                return window.fetch(url, init);
            }
        });
    }, [props.StoreDomain, props.PublicAccessToken]);
    const windowSize = useWindowSize();
    const CART_KEY = `$shopifyCartId_${props.StoreDomain}`;
    const routes = useRecoilValue(atoms.routes);

    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState(props.Keywords);
    const [loading, setLoading] = useState(true);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [productDetailOpen, setProductDetailOpen] = useState(false);
    const [productToView, setProductToView] = useState(null);
    const [cartOpen, setCartOpen] = useState(false);
    const [cart, setCart] = useState(null);
    const [productCursor, setProductCursor] = useState(null);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [activeImageIndexes, setActiveImageIndexes] = useState({});
    const [selectedQuantities, setSelectedQuantities] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [sortKey, setSortKey] = useState(props.SortOrder);
    const [sortReverse, setSortReverse] = useState(false);
    const [showSortDropdown, setShowSortDropdown] = useState(false);
    const [showMobileSortDropdown, setShowMobileSortDropdown] = useState(false);
    const [selectedSortOption, setSelectedSortOption] = useState(props.SortOrder);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [totalProducts, setTotalProducts] = useState(props.TotalProducts);

    const cartCount = cart?.totalQuantity || 0;
    const cartItems = cart?.lines?.edges || [];

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sortParam = Number(params.get('sort') || 1);
        const existingCartId = localStorage.getItem(CART_KEY);
        if (existingCartId) {
            refreshCart(existingCartId);
        }

        const selectedSort = getSortOptionByNumber(sortParam);
        setSelectedSortOption(selectedSort.Key);
        setSortKey(selectedSort.sortKey);
        setSortReverse(selectedSort.reverse);

        loadProducts(
            false,
            searchTerm,
            selectedSort.sortKey,
            selectedSort.reverse
        );
    }, []);

    const getSortOptionByNumber = (sortNumber) =>
        SORT_OPTIONS.find(option => option.Key === Number(sortNumber)) || SORT_OPTIONS[0];

    const toggleMobileSortDropdown = () => setShowMobileSortDropdown(prev => !prev);
    const toggleMobileFilters = () => setShowMobileFilters(prev => !prev);
    const toggleProductDetail = () => setProductDetailOpen(productDetailOpen => !productDetailOpen);
    const renderSortModal = () => {
        return (
            <Modal containerClassName='sort-option-container' onEscapeKeyClose={toggleMobileSortDropdown}>
                <Modal.Header onClose={toggleMobileSortDropdown}>
                    <Modal.Title>
                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.sortby' />
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p id='description-label' className='hidden'>Sort results</p>

                    {SORT_OPTIONS.map((sortItem) => (
                        <a
                            role="button"
                            tabIndex="0"
                            key={'mobile-' + sortItem.Key}
                            className={sortItem.Key === selectedSortOption
                                ? 'sort-option selected-option'
                                : 'sort-option'}
                            onClick={() => handleSortChange(sortItem.Key)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    handleSortChange(sortItem.Key);
                                }
                            }}
                        >
                            <strong>{sortItem.Value}</strong>
                        </a>
                    ))}
                </Modal.Body>
            </Modal>
        )
    };

    const updateURLWithSort = (sortNumber) => {
        const currentURL = window.location.href;
        const urlWithoutSort = removeURLParameter(currentURL, 'sort');
        const newURL = insertURLParameter(urlWithoutSort, 'sort', sortNumber);
        window.history.replaceState(null, '', newURL);
    };

    const updateURLWithSearch = (searchTerm) => {
        const currentURL = window.location.href;
        const urlWithoutSearch = removeURLParameter(currentURL, 'keywords');
        if (searchTerm != '') {
            const newURL = insertURLParameter(urlWithoutSearch, 'keywords', searchTerm);
            window.history.replaceState(null, '', newURL);
        }
        else {
            window.history.replaceState(null, '', urlWithoutSearch);
        }
    };

    const getMaxQuantity = (value) => (value === null || value === undefined ? null : Math.max(1, value));
    const getSelectedQuantity = (productId) => selectedQuantities[productId] ?? 1;
    const setProductSelectedQuantity = (productId, quantity, maxQuantity = null) => {
        const nextQuantity = Math.max(1, quantity);
        const clampedQuantity = maxQuantity == null ? nextQuantity : Math.min(nextQuantity, maxQuantity);

        setSelectedQuantities(prev => ({
            ...prev,
            [productId]: clampedQuantity
        }));
    };

    const getDisplayedProductCount = () => filteredProducts.length;
    const mapProductEdges = (edges) =>
        edges.map(edge => {
            const p = edge.node;
            const images = p.images?.edges?.map(imageEdge => ({
                id: imageEdge.node.id,
                url: imageEdge.node.url,
                altText: imageEdge.node.altText || p.title
            })) || [];

            return {
                id: p.id,
                title: p.title,
                vendor: p.vendor || '',
                category: p.category?.name || '',
                productType: p.productType || '',
                variantId: p.variants.edges[0]?.node?.id || '',
                images,
                imageUrl: images[0]?.url || '',
                price: p.variants.edges[0]?.node?.price?.amount || 0.00,
                currencyCode: p.variants.edges[0]?.node?.price?.currencyCode || 'USD',
                description: p.descriptionHtml || '',
                tags: p.tags,
            };
        });

    const getActiveImageIndex = (productId) => activeImageIndexes[productId] ?? 0;

    const setProductImageIndex = (productId, index) => {
        setActiveImageIndexes(prev => ({
            ...prev,
            [productId]: index
        }));
    };

    const handlePrevImage = (productId, imageCount) => {
        if (!imageCount) return;
        setActiveImageIndexes(prev => ({
            ...prev,
            [productId]: (prev[productId] ?? 0) === 0 ? imageCount - 1 : (prev[productId] - 1)
        }));
    };

    const handleNextImage = (productId, imageCount) => {
        if (!imageCount) return;
        setActiveImageIndexes(prev => ({
            ...prev,
            [productId]: ((prev[productId] ?? 0) + 1) % imageCount
        }));
    };

    const formatMoney = (amount, currencyCode, locale) => {
        const resolvedLocale =
            locale ||
            (typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US');

        return new Intl.NumberFormat(resolvedLocale, {
            style: 'currency',
            currency: currencyCode || 'USD'
        }).format(Number(amount || 0));
    };

    const cartSubtotal = cart?.cost?.subtotalAmount;

    const escapeQueryValue = (value) => `"${String(value).replace(/"/g, '\\"')}"`;

    const loadProducts = async (
        append = false,
        queryOverride = searchTerm,
        sortOptionOverride = sortKey,
        sortReverseOverride = sortReverse
    ) => {
        if (append) setLoadingMore(true);

        try {
            const response = await client.request(getProductsQuery, {
                variables: {
                    after: append ? productCursor : null,
                    query: queryOverride || null,
                    sortKey: sortOptionOverride,
                    reverse: sortReverseOverride,
                }
            });

            let edges = response.data?.products?.edges || [];
            const pageInfo = response.data?.products?.pageInfo;
            const items = mapProductEdges(edges);

            setProducts(prev => {
                const next = append ? [...prev, ...items] : items;
                setFilteredProducts(next);
                return next;
            });

            setProductCursor(pageInfo?.endCursor || null);
            setHasMoreProducts(!!pageInfo?.hasNextPage);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const handleSortChange = (sortNumber) => {
        const selectedSort = getSortOptionByNumber(sortNumber);

        setSelectedSortOption(selectedSort.Key);
        setSortKey(selectedSort.sortKey);
        setSortReverse(selectedSort.reverse);

        updateURLWithSort(selectedSort.Key);

        setProductCursor(null);
        setHasMoreProducts(true);
        toggleMobileSortDropdown();

        loadProducts(
            false,
            searchTerm,
            selectedSort.sortKey,
            selectedSort.reverse
        );
    };

    const refreshCart = async (cartId) => {
        const response = await client.request(getCartQuery, { variables: { id: cartId } });
        setCart(response.data?.cart || null);
    };

    const getCartLineByVariantId = (variantId) =>
        cart?.lines?.edges?.find(({ node }) => node?.merchandise?.id === variantId)?.node || null;

    const isProductInCart = (variantId) => !!getCartLineByVariantId(variantId);

    const handleUpdateCartLineQuantity = async (line, nextQuantity) => {
        const existingCartId = localStorage.getItem(CART_KEY);
        if (!existingCartId || !line) return;

        const maxQuantity = getMaxQuantity(line.merchandise?.quantityAvailable);
        const clampedQuantity = Math.max(
            1,
            maxQuantity === null ? nextQuantity : Math.min(nextQuantity, maxQuantity)
        );

        await client.request(cartLinesUpdateMutation, {
            variables: {
                cartId: existingCartId,
                lines: [{ id: line.id, quantity: clampedQuantity }]
            }
        });

        await refreshCart(existingCartId);
    };

    const handleAddToCart = async (product, quantity = 1) => {
        const variantId = product.variantId;
        if (!variantId) return;
        const maxQuantity = getMaxQuantity(product.availableQuantity);
        const existingCartId = localStorage.getItem(CART_KEY);
        const existingLine = getCartLineByVariantId(variantId);
        const existingQuantity = existingLine?.quantity || 0;

        const desiredQuantity = Math.max(1, quantity);
        const finalQuantity = maxQuantity === null
            ? desiredQuantity
            : Math.min(desiredQuantity, maxQuantity);

        if (!existingCartId) {
            const response = await client.request(cartCreateMutation, {
                variables: { lines: [{ merchandiseId: variantId, quantity: finalQuantity }] }
            });

            const createdCart = response.data?.cartCreate?.cart;
            if (createdCart?.id) {
                localStorage.setItem(CART_KEY, createdCart.id);
                await refreshCart(createdCart.id);
                setProductDetailOpen(false);
                setCartOpen(true);
            }
            return;
        }

        if (existingLine) {
            await handleUpdateCartLineQuantity(existingLine, existingQuantity + finalQuantity);
        } else {
            await client.request(cartLinesAddMutation, {
                variables: {
                    cartId: existingCartId,
                    lines: [{ merchandiseId: variantId, quantity: finalQuantity }]
                }
            });

            await refreshCart(existingCartId);
        }

        setProductDetailOpen(false);
        setCartOpen(true);
    };

    const handleRemoveFromCart = async (product) => {
        const variantId = product.variantId;
        if (!variantId) return;

        const existingCartId = localStorage.getItem(CART_KEY);
        if (!existingCartId || !cart) return;

        const cartLine = getCartLineByVariantId(variantId);
        if (!cartLine) return;

        await client.request(cartLinesRemoveMutation, {
            variables: {
                cartId: existingCartId,
                lineIds: [cartLine.id]
            }
        });

        await refreshCart(existingCartId);
    };

    const applyQuickSearch = (value) => {
        setSearchTerm(value);
        updateURLWithSearch(value);

        setProductCursor(null);
        setHasMoreProducts(true);

        if (windowSize?.isMobile) {
            setShowMobileFilters(false);
        }
        //get the total count of products that match the search term and update the product count display
        sandhillsRequest({
            url: routes.GetTotalProductsCount,
            method: 'post',
            data: {
                keywords: value,
                storeDomain: props.StoreDomain,
                settingsReferenceID: props.SettingsReferenceID
            }
        }).then((data) => {
            const totalProducts = data?.totalProducts || 0;
            setTotalProducts(totalProducts);
        });

        loadProducts(false, value);
    };

    const handleSearch = (e) => {
        applyQuickSearch(e.target.value);
    };

    const handleQuickSearchClear = () => {
        applyQuickSearch('');
    };

    const modalQuantity = getSelectedQuantity(productToView?.id);
    const modalMaxQuantity = getMaxQuantity(productToView?.availableQuantity);
    const modalProductImages =
        productToView?.images?.length > 0
            ? productToView.images
            : null;

    const modalActiveImageIndex = getActiveImageIndex(productToView?.id);
    const modalActiveImage = modalProductImages ? (modalProductImages[modalActiveImageIndex] || modalProductImages[0]) : null;

    const formatProductCount = (count) => {
        if (count >= 10000) {
            return count.toLocaleString() + "+";
        }
        return count.toLocaleString();
    };

    return (
        <div className="shopify">
            {!windowSize?.isMobile &&
                <CartButton cartCount={cartCount} setCartOpen={setCartOpen} />}
            <Cart
                cart={cart}
                cartOpen={cartOpen}
                setCartOpen={setCartOpen}
                cartItems={cartItems}
                handleRemoveFromCart={handleRemoveFromCart}
                handleUpdateCartLineQuantity={handleUpdateCartLineQuantity}
                cartSubtotal={cart?.subtotal}
                formatMoney={formatMoney}
                getMaxQuantity={getMaxQuantity}
            />
            <div className="shopify-header-wrapper">
                <Typography variant="h4" component="h1" gutterBottom style={{ fontWeight: 700, fontFamily: 'Arial, sans-serif', fontSize: '1.8rem' }}>
                    {props.PageTitle}
                </Typography>                
                <div className="product-count">
                    <ProductCount>{filteredProducts.length > 0 ? 1 : 0} - {getDisplayedProductCount()} of {formatProductCount(totalProducts)} Products</ProductCount>
                </div>
            </div>
            {((windowSize?.isMobile && showMobileFilters) || !windowSize?.isMobile) &&
                <SearchContainer
                    {...props}
                    setSearchTerm={setSearchTerm}
                    handleSearch={handleSearch}
                    handleQuickSearchClear={handleQuickSearchClear}
                    searchTerm={searchTerm}
                    toggleMobileFilters={toggleMobileFilters}
                    showMobileFilters={showMobileFilters}
                />}
            {loading ? <LoadingRing sxBox={{ 'justify-content': 'center', 'height': '50px', 'margin-top': '20px', 'margin-bottom': '20px' }} /> :
                <div id="main-content" className={windowSize.width > 1000 ? "list-main-section has-search-bar" : "list-main-section no-search-bar"}>
                    <div className='shopify-main'>
                        {!windowSize?.isMobile &&
                            <div className="show-closest-first-container">
                                <div className="show-closest-first">
                                    <div className="right">
                                        <div role="button" tabIndex="0" className="sort-button-spoof" id="option-bar-sort" style={{ height: 'auto' }} onClick={toggleMobileSortDropdown} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); toggleMobileSortDropdown(); } }}><i className="fas fa-sort-amount-down"></i>
                                            <br />
                                            <span>
                                                <span>Sort</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>}
                        <div className='grid' style={{
                            padding: '11px 20px 20px 20px',
                            flexWrap: 'wrap',
                            gap: '5px'
                        }}>
                            {filteredProducts.length > 0 && filteredProducts.map(product => {
                                return <ProductListing key={product.id} product={product} formatMoney={formatMoney} handleAddToCart={handleAddToCart} handleRemoveFromCart={handleRemoveFromCart} isProductInCart={isProductInCart} setProductToView={setProductToView} toggleProductDetail={toggleProductDetail} getActiveImageIndex={getActiveImageIndex} setProductImageIndex={setProductImageIndex} handlePrevImage={handlePrevImage} handleNextImage={handleNextImage} />;
                            })}
                            <div style={{ width: '100%', textAlign: 'center', marginTop: '20px', marginBottom: '50px' }}>
                                <Button
                                    variant="contained"
                                    onClick={() => loadProducts(true)}
                                    disabled={!hasMoreProducts || loadingMore}
                                    style={{ margin: '8px' }}
                                >
                                    {loadingMore ? 'Loading...' : hasMoreProducts ? 'View More' : 'No More Products'}
                                </Button>
                            </div>
                            {productDetailOpen && productToView &&
                                <ProductDetail {...props} productToView={productToView} formattedPrice={formatMoney(productToView.price, productToView.currencyCode)} modalQuantity={modalQuantity} modalMaxQuantity={modalMaxQuantity} modalProductImages={modalProductImages} modalActiveImageIndex={modalActiveImageIndex} modalActiveImage={modalActiveImage} handleAddToCart={handleAddToCart} toggleProductDetail={toggleProductDetail} handleNextImage={handleNextImage} handlePrevImage={handlePrevImage} setProductImageIndex={setProductImageIndex} />
                            }
                        </div>
                    </div>
                    {windowSize?.isMobile &&
                        <div className="bottom-bar bottom-bar--visible">
                            <div className='mobile-listing-control-bar'>
                                <div className='left-half'>
                                    <div className="bar-item nav-sort" id="bar-item-1">
                                        <div role="button" tabIndex="0" className="option-bar-option" id="option-bar-sort" style={{ height: '39.5px' }} onClick={() => setCartOpen(true)}>
                                            <div style={{ padding: '5px', height: '32px' }}>
                                                <Badge badgeContent={cartCount} color="error">
                                                    <ShoppingCartIcon />
                                                </Badge>
                                            </div>
                                            <br />
                                            <div>
                                                <span className="uppercase">
                                                    <span>Cart</span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bar-item bi-center" id="bar-item-center">
                                    <a role="button" tabIndex="0" onClick={toggleMobileFilters}>
                                        <div className="group">
                                            <i className="fas fa-filter"></i>
                                            <i className="fas fa-sort small-sort"></i>
                                        </div>
                                        <span>Filter</span>
                                    </a>
                                </div>
                                <div class="bar-item"></div>
                                <div className="right-half">
                                    <div className="bar-item nav-sort" id="bar-item-2">
                                        <div role="button" tabIndex="0" className="option-bar-option" id="option-bar-sort" style={{ height: '39.5px' }} onClick={toggleMobileSortDropdown}>
                                            <i className="fas fa-sort-amount-down"></i>
                                            <br />
                                            <span className="uppercase">
                                                <span>Sort</span>
                                            </span>
                                        </div>
                                        {showMobileSortDropdown &&
                                            renderSortModal()
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                    {showMobileSortDropdown && !windowSize?.isMobile && 
                        renderSortModal()
                    }
                </div>
            }
        </div>
    );
};

export default ShopView;
global.ShopView = ShopView;